import { computed } from 'vue';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { useCollectionsStore } from '@/store/useCollectionsStore';

export const useRequestContents = () => {
  const store = useRequestContentStore();
  const collectionsStore = useCollectionsStore();

  // Always load collections to ensure fresh data
  const ensureCollectionsLoaded = async () => {
    // Always load collections regardless of current state to ensure data is fresh
    // This prevents "Unknown Collection" when navigating from different pages
    await collectionsStore.load();
  };

  // Display pages for pagination - this adds business logic not in store
  const displayPages = computed(() => {
    const pages: number[] = [];
    const current = store.meta.currentPage;
    const total = store.meta.totalPages;

    // Simple pagination logic - show up to 5 pages around current
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  });

  // Actions - only keep actions that combine multiple stores
  const loadRequestContentsWithCollections = async (page?: number, perPage?: number) => {
    // Update store pagination parameters if provided
    if (page !== undefined) {
      store.meta.currentPage = page;
    }
    if (perPage !== undefined) {
      store.meta.perPage = perPage;
    }

    await Promise.all([store.loadRequestContents(), ensureCollectionsLoaded()]);
  };

  // Pagination actions
  const changePage = async (page: number) => {
    await store.changePage(page);
    await ensureCollectionsLoaded();
  };

  const changePerPage = async (perPage: number) => {
    if (perPage < 1 || perPage > 100) return;

    store.meta.perPage = perPage;
    store.meta.currentPage = 1; // Reset to first page
    await loadRequestContentsWithCollections();
  };

  return {
    // State - direct access to store data, no enrichment
    requestContents: computed(() => store.requestContents),
    displayPages,
    // Pagination info directly from store
    meta: computed(() => store.meta),
    isLoading: computed(() => store.isLoading),
    hasMultiplePages: computed(() => store.hasMultiplePages),
    isFirstPage: computed(() => store.isFirstPage),
    isLastPage: computed(() => store.isLastPage),
    // Actions
    loadRequestContentsWithCollections,
    changePage,
    changePerPage,
    // URL validation methods
    checkCustomUrlAvailability: store.checkCustomUrlAvailability,
    updateRequestContent: store.updateRequestContent,
  };
};
